import { useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../../services/api';
import type { DiagnosticReport } from '../../../types/diagnostics';
import {
  REPORT_POLL_MS,
  diagnoseErrorOf,
  isReportInFlight,
  rememberSymptom,
  reportPollInterval,
  type DiagnoseError,
} from './reportLogic';

export const diagnosticsListKey = (deviceId: string) => ['device-diagnostics', deviceId] as const;
export const diagnosticReportKey = (deviceId: string, reportId: string | null) =>
  ['device-diagnostic-report', deviceId, reportId] as const;

/**
 * One report, re-read every few seconds while Claude is still working on it.
 * When it settles, the bundle list is refreshed so the row's chip matches.
 */
export function useDiagnosticReport(
  deviceId: string,
  reportId: string | null,
  { pollMs = REPORT_POLL_MS }: { pollMs?: number } = {},
) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: diagnosticReportKey(deviceId, reportId),
    queryFn: () => api.adminGetDiagnosticReport(deviceId, reportId!),
    enabled: !!reportId,
    refetchInterval: (q) => reportPollInterval((q.state.data as DiagnosticReport | undefined)?.status, pollMs),
  });

  const status = query.data?.status;
  const wasInFlight = useRef(false);
  useEffect(() => {
    if (isReportInFlight(status)) {
      wasInFlight.current = true;
    } else if (status && wasInFlight.current) {
      wasInFlight.current = false;
      queryClient.invalidateQueries({ queryKey: diagnosticsListKey(deviceId) });
    }
  }, [status, deviceId, queryClient]);

  return query;
}

export type StartDiagnosisResult = { reportId: string } | { error: DiagnoseError };

/**
 * Asks Claude to diagnose a bundle. A 409 means one is already running for
 * that bundle — rather than an error, the caller gets that report to follow.
 * Other refusals (daily cap, not configured) come back as `error`.
 */
export function useStartDiagnosis(deviceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ bundleId, symptom }: { bundleId: string; symptom?: string }): Promise<StartDiagnosisResult> => {
      rememberSymptom(deviceId, symptom);
      try {
        const report = await api.adminDiagnoseBundle(deviceId, bundleId, symptom);
        return { reportId: report.id };
      } catch (err) {
        const error = diagnoseErrorOf(err);
        if (error.kind !== 'already-running') return { error };
        if (error.reportId) return { reportId: error.reportId };
        const reports = await api.adminListDiagnosticReports(deviceId, bundleId).catch(() => []);
        const running = reports.find((r) => isReportInFlight(r.status));
        return running ? { reportId: running.id } : { error };
      }
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: diagnosticsListKey(deviceId) }),
  });
}
